import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PageEnums } from 'src/app/shared/enum/page-enums';
import { SortOrder } from 'src/app/shared/enum/sort-order';
import { PageInfo } from 'src/app/shared/model/page-info.model';
import { MailInfo } from 'src/app/shared/model/mail-info.model';
import { SearchCondition } from 'src/app/shared/model/search-condition.model';
import { SearchCount } from 'src/app/shared/model/search-count.model';
import { SearchPage } from 'src/app/shared/model/search-page.model';
import { MatchingOffer } from 'src/app/shared/model/matching-offer.model';
import { NewReceivedMailService } from 'src/app/shared/service/new-received-mail.service';
import { OfferService } from 'src/app/shared/service/offer.service';
import { SearchConditionService } from 'src/app/shared/service/search-condition.service';
import { SearchWorkerService } from 'src/app/shared/service/search/search-worker.service';
import { SearchWorkerProjectService } from 'src/app/shared/service/search/search-worker-project.service';
import { StringService } from 'src/app/shared/service/string.service';

@Component({
  selector: 'app-matching-worker-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class MatchingWorkerListComponent implements OnInit, OnDestroy {
  closeSearch = false;
  pageInfo: PageInfo;
  workers: Array<MailInfo> = [];
  userFocusMails: number[];
  readMails: number[];
  sharedAccountOffers: MatchingOffer[]; // 共通アカウント（USER_CODE）に紐付くオファーを取得
  offeredMailIds: number[]; // offersの中のmail_id
  loading = false;
  page = 1;
  openMailId = -1;
  autoLoadCount = 0;

  sortParams = {
    date_order: SortOrder.desc,
    fee_order: SortOrder.unSort,
    age_order: SortOrder.unSort
  };

  displaySenderName = true;
  titleKeywordOrder = true; // true: title + keywords, false: keywords + title
  searchParams: SearchCondition;

  // subscriptions
  stateSub$: Subscription;
  nextPageLoad$: Subscription;
  searchFocusSub$: Subscription;
  searchReadMailSub$: Subscription;
  tabClickState$: Subscription;
  offerSub$: Subscription;
  private workerRuleSubscription: Subscription;
  private focusMailSubscription: Subscription;
  private readMailSubscription: Subscription;
  private bestMailSubscription: Subscription;

  listPage: string = PageEnums.worker_list;

  constructor(
    private router: Router,
    private newReceivedMailService: NewReceivedMailService,
    private offerService: OfferService,
    private searchConditionService: SearchConditionService,
    public searchWorkerService: SearchWorkerService,
    private searchWorkerProjectService: SearchWorkerProjectService,
    private stringService: StringService,
  ) {}

  ngOnInit() {
    this.workerRuleSubscription = this.searchConditionService.getWorkerRule().subscribe(res => {
      this.searchParams = new SearchCondition(res.body.default_search_rule);
      this.searchWorkerService.setSearchCondition(this.searchParams);
    });
    // イベント受信時の処理
    this.getSubscriptions();
    // 初回読み込み処理
    this.newSearch();
  }

  ngOnDestroy() {
    if (this.stateSub$) {
      this.stateSub$.unsubscribe();
    }
    if (this.nextPageLoad$) {
      this.nextPageLoad$.unsubscribe();
    }
    if (this.offerSub$) {
      this.offerSub$.unsubscribe();
    }
    if (this.workerRuleSubscription) {
      this.workerRuleSubscription.unsubscribe();
    }
    if (this.focusMailSubscription) {
      this.focusMailSubscription.unsubscribe();
    }
    if (this.readMailSubscription) {
      this.readMailSubscription.unsubscribe();
    }
    if (this.bestMailSubscription) {
      this.bestMailSubscription.unsubscribe();
    }
  }

  // イベント受信時の処理
  // TODO: 現状おそらく初回読み込み時などで購読できているが、他にも購読できるようにすべき箇所があると考えられる（onScrollなど）
  getSubscriptions(): void {
    this.stateSub$ = this.searchWorkerService.searchState.subscribe(
      res => {
        if (this.nextPageLoad$) {
          this.nextPageLoad$.unsubscribe();
        }
        this.openMailId = -1;
        this.loading = false;
        this.workers = res.workers;

        // sessionStorageにworkersのidを保存
        sessionStorage.workerIds = this.workers.map(worker => worker.id);

        // メールに紐付く情報を取得
        this.getAdditionalMailInfo();

        // ページの表示情報更新
        this.setSearchCount(res);
        this.pageInfo = this.searchWorkerService.getPageInfo(); // TODO: 未使用の可能性あり
        this.page = res.page;

        this.autoLoadCount = 1;
        this.autoLoad();
        this.newReceivedMailService.initCount('workers');
      },
      error => {
        this.loading = false;
      }
    );
  }

  // 注力フラグ, 既読フラグ, オファー, ベストメール情報を取得する関数
  getAdditionalMailInfo(): void {
    // 注力状況の読み込み
    this.focusMailSubscription = this.searchWorkerService.searchFocusMail().subscribe(res => {
      this.userFocusMails = res.body.focus_ids;
      this.workers = this.workers.map(worker => {
        if (this.userFocusMails.includes(worker.id)) {
          worker.user_focus = true;
        }
        return worker;
      });
    });
    // 既読フラグ取得
    this.readMailSubscription = this.searchWorkerService.searchReadMail().subscribe(res => {
      this.readMails = res.body.read_mail_ids;
    });
    // 共通アカウント（USER_CODE）に紐付くオファーを取得
    this.offerSub$ = this.offerService.getSharedOffers().subscribe(res => {
      this.sharedAccountOffers = res.body.offers;
      this.offeredMailIds = this.sharedAccountOffers.map(
        offer => offer.worker_mail_id
      );
    });
    // ベストメール取得
    this.bestMailSubscription = this.searchWorkerService.getBestMails().subscribe(res => {
      let bestMails = res.body.best_mails;
      this.workers.forEach(worker => {
        let hash = this.stringService.zenkakuToHankakuAndUppercase(worker.sender_hash_id);
        if (bestMails[hash]) {
          worker.is_best = bestMails[hash] == worker.fee;
        }
      });
    });
  }

  // ページ数情報をresから取得 => sessionStorage保存
  setSearchCount(res: any) {
    const searchCount = new SearchCount({
      startCount: res.pageInfo.start_count,
      endCount: res.pageInfo.end_count,
      totalCount: res.pageInfo.total,
      perPage: this.searchWorkerService.getSearchCount().perPage
    });
    this.searchWorkerService.setSearchCount(searchCount);
  }

  loadingStart() {
    this.loading = true;
    this.workers = [];
    this.sortParams = {
      date_order: SortOrder.desc,
      fee_order: SortOrder.unSort,
      age_order: SortOrder.unSort
    };
  }

  loadingFinish() {}

  // 検索条件のセットのみ実行
  // （実際の初回読み込みはSearchSectionComponentを通して行われる）
  newSearch() {
    this.loading = true;
    this.workers = [];

    // ページ数をsessionにセット;
    this.searchWorkerService.setSearchPage(new SearchPage({ page: 1 }));

    this.searchWorkerService.search$.next({
      id: '',
      additionalParams: this.getSortParams()
    });
  }

  // メール総数が50件以下、且つautoLoad()実行回数が10回以下の場合、自動で次のページを読み込む
  // 一覧ページから移動した時に処理を終了するため、indexOf()でURLを確認する
  // TODO: 本当にindexOfの条件が必要か要確認
  autoLoad(): void {
    if (this.router.url.indexOf('worker') !== -1) {
      this.autoLoadCount += 1;
      if (this.workers.length < 50 && this.autoLoadCount < 10) {
        this.onScroll();
      }
    }
  }

  onScroll() {
    if (!this.loading) {
      this.loading = true;
      // ページ数をAPIに送信する時に、sessionStorageに格納された値を使用しているので、ここでsessionStorageに格納
      this.searchWorkerService.setSearchPage(
        new SearchPage({ page: this.page + 1 })
      );
      if (this.nextPageLoad$) {
        this.nextPageLoad$.unsubscribe();
      }
      this.nextPageLoad$ = this.searchWorkerService
        .search('', this.getSortParams())
        .subscribe(res => {
          // 無限スクロールに対する制御
          this.scrollToAnchor(res.body.workers.length > 0);

          // 検索結果をworkersに追加
          // TODO: 本来はgetSubscriptions()で購読できるようnextなどを使うべきだと考えられる
          // （その場合unsubscribe()のタイミングなども要確認）
          this.workers = this.workers.concat(res.body.workers);

          // メールに紐付く情報を取得
          this.getAdditionalMailInfo();

          // sessionStorageにworkersのidを保存
          sessionStorage.workerIds = this.workers.map(worker => worker.id);

          this.loading = false;
          this.page = res.body.page;
          this.autoLoad();
        });
    }
  }

  getSortParams() {
    const order = {};

    for (const [key, value] of Object.entries(this.sortParams)) {
      if (value !== SortOrder.unSort) {
        order[key] = SortOrder[value];
      }
    }
    return order;
  }

  loadMailDetail(mailId: number): void {
    if (!this.workers[mailId].body) {
      this.searchWorkerProjectService
        .showWorkerMail(this.workers[mailId].id.toString())
        .subscribe(showRes => {
          Object.assign(this.workers[mailId], showRes.body.worker);
        });
    }
  }

  // スクロールしてメールを読み込んだ時に、追加で取得したメールがない場合
  // 要素'scroll-loading-anchor'（更に検索ボタン）を最下部に固定する。
  // そうすることで onScroll()が永遠と実行されないようにする。
  // おそらくOverviewListComponent内で無限スクロール(infiniteScroll)を実行しているため
  // この処理が必要になっているのだと考えられる。
  // TODO: この関数自体をOverviewListComponentに移行した方が自然かもしれない
  scrollToAnchor(new_mail_loaded: boolean) {
    const element = document.getElementById('scroll-loading-anchor');
    if (
      !new_mail_loaded &&
      element &&
      !(this.workers.length < 50 && this.autoLoadCount < 10)
    ) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  }
}
